import React, { useRef, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { getDevice, site } from '@panel/commons/helpers';
import { sendCustomTrackingInfo } from '@panel/commons';
import { usePreventWindowUnload, useQuery } from '@panel/commons/hooks';
import { SavePostingOperations } from '../../../ducks/savePosting';
import { OperationType } from './OperationType';
import { PropertyType } from './PropertyType';
import { PropertySubType } from './PropertySubType';
import { getStepMainSelectors, StepMainOperations } from '../../../ducks/propertyType';
import { isPortalWithPropertySubType, getSortedPropertyTypes } from '../../../utils';
import { WrapperForm } from './styles';
import WrapperContainer from '../../Wrappers/WrapperContainer';
import { mainSchema } from './validations';
import { bodyManagerMainStep } from '../../../utils/bodyManagerMainStep';
import useFormIsDirty from '../../../hooks/useFormIsDirty';
import { StepperOperations, StepperSelectors } from '../../../ducks/stepper';
import { adaptPublicationStatus, propertyTypeNameSelected } from '../../../utils/adaptStepDataAndSendTrackinInfo';

const MainData = () => {
  const device = getDevice();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const formRef = useRef(null);
  const [sortedPropertyTypesMostUsed, setSortedPropertyTypesMostUsed] = useState([]);
  const [sortedPropertyTypes, setSortedPropertyTypes] = useState([]);
  const [sortedPropertySubTypes, setSortedPropertySubTypes] = useState([]);
  const [hasInitializedForm, setHasInitializedForm] = useState(false);

  const [postingId, isEdition, isDraftFromPanel] = useQuery(useLocation(), [
    'postingId',
    'isEdition',
    'isDraftFromPanel'
  ]);
  const { setFormIsDirty } = useFormIsDirty();
  const { snackAction } = useSelector(StepperSelectors.getStepperState);
  const {
    propertyTypesMostUsedOptions,
    propertyTypeOptions,
    formValues,
    propertyTypesLoading,
    formValuesCompleted,
    formValuesLoading,
    propertyTypesError,
    formValuesError,
    propertySubTypeOptions,
    propertySubTypeLoading,
    propertySubTypeError
  } = useSelector(getStepMainSelectors);
  const { propertyType, propertySubType } = formValues;

  const { register, handleSubmit, setValue, watch, clearErrors, reset, errors, formState, trigger } = useForm({
    defaultValues: formValues,
    resolver: yupResolver(mainSchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit'
  });
  const isEnableBackButton = (!postingId && !site.isBrasil) || isEdition;
  const watchedOperationType = watch('operationType');
  const watchedPropertyType = watch('propertyType');
  const watchedPropertySubType = watch('propertySubType');

  const effectivePropertySubType =
    postingId && hasInitializedForm && !formState.isDirty
      ? propertySubType // Si el formulario NO ha sido modificado usamos el valor de Redux  (recién cargado)
      : watchedPropertySubType; // En cambio, una vez que el usuario interactúa, usamos el watched directamente

  usePreventWindowUnload(formState.isDirty);

  /**
   * Maneja cambios en los campos del formulario y elimina los errores del form y capturamos la data para crear el draft del aviso.
   * @param {string} fieldName - Nombre del campo que cambió
   * @param {*} value - Nuevo valor del campo
   */
  const handleFieldChange = (fieldName, value) => {
    setValue(fieldName, value, { shouldDirty: true });
    clearErrors(fieldName);
    clearErrors('operationAndPropertyEmpty');
  };

  /**
   * Maneja la obtención de los subtipos de propiedad en base al tipo de propiedad seleccionado.
   * @param {number} realEstateTypeId - ID del tipo de propiedad seleccionado
   * @return {void}
   */
  const getSubPropertyType = realEstateTypeId => {
    if (isPortalWithPropertySubType) {
      dispatch(StepMainOperations.getSubPropertyType(realEstateTypeId));
    }
  };

  const onSubmit = data => {
    const payload = {
      body: { ...bodyManagerMainStep(data), postingId },
      step: 'STEP_OPERATION'
    };
    dispatch(SavePostingOperations.savePostingDB(payload));
  };

  // 1. INICIALIZACIÓN - Al montar el componente
  useEffect(() => {
    // Dispatch para traer los tipos de propiedades y los más usados:
    dispatch(StepMainOperations.getPropertyType());
    // Si hay postingId, reseteamos el estado para forzar recarga de datos del servidor
    if (postingId) {
      dispatch(StepMainOperations.resetInfo());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2. FETCH DE DATOS DEL SERVIDOR - Traemos los datos guardados del aviso cuando hay postingId
  useEffect(() => {
    if (postingId && !formValuesCompleted && !formValuesLoading && !formValuesError) {
      dispatch(StepMainOperations.getFormValues(postingId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postingId, formValuesCompleted, formValuesLoading]);

  // 3. INICIALIZACIÓN DEL FORMULARIO - Cuando llegan los datos del servidor, inicializamos el formulario
  useEffect(() => {
    if (postingId && formValuesCompleted && !hasInitializedForm && !formValuesLoading) {
      reset(formValues);
      setHasInitializedForm(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValuesCompleted, formValuesLoading, hasInitializedForm]);

  // 4.1 MANEJO DE SUBTIPOS DE PROPIEDAD - Traemos los subtipos cuando hay un tipo de propiedad seleccionado
  useEffect(() => {
    if (postingId && propertyType && propertySubTypeOptions.length === 0) {
      getSubPropertyType(propertyType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postingId, propertyType]);

  // 4.2 CASO Nueva creación - usuario cambió a un tipo de propiedad sin subtipos
  useEffect(() => {
    // Si no hay postingId, ya hay un subtipo seleccionado y se selecciona otro tipo de propiedad que no tiene subtipos limpiamos el subtipo
    if (!postingId && watchedPropertySubType && propertySubTypeOptions.length === 0) {
      setValue('propertySubType', null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postingId, watchedPropertySubType, propertySubTypeOptions.length]);

  // 5. ACTUALIZACIÓN DE ESTADOS LOCALES
  useEffect(() => {
    // Actualizar opciones ordenadas cuando cambian
    setSortedPropertyTypesMostUsed(propertyTypesMostUsedOptions);
    setSortedPropertyTypes(getSortedPropertyTypes(propertyTypeOptions));
    setSortedPropertySubTypes(getSortedPropertyTypes(propertySubTypeOptions));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyTypesMostUsedOptions, propertyTypeOptions, propertySubTypeOptions]);

  // 6. ENVÍO DE DL'S
  useEffect(() => {
    if (!propertyTypesError && !formValuesError) {
      sendCustomTrackingInfo({
        event: 'create_publication',
        content_group: 'Publicador',
        user_type: 'Profesional',
        step: 'Paso 0 - Visualización del publicador',
        status: adaptPublicationStatus(isEdition, isDraftFromPanel),
        posting_id: postingId || 'borrador generado'
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 7. MANEJO DE ERRORES Y SNACKBARS
  useEffect(() => {
    const showErrorSnackbar = (actionKey, message) => {
      dispatch(
        StepperOperations.setSnackAction({
          action: actionKey,
          result: 'ERROR',
          component: message,
          section: 'Publicador profesionales - Caracteristicas principales',
          preventDuplicateActions: true,
          preventStacking: true
        })
      );
    };

    if (errors.operationType) {
      showErrorSnackbar('operationTypeEmpty', t('professionalPubli.main.error.operationType'));
    }
    if (errors.propertyType) {
      showErrorSnackbar('propertyTypeEmpty', t('professionalPubli.main.error.propertyType'));
    }
    if (errors.operationAndPropertyEmpty) {
      showErrorSnackbar('operationAndPropertyTypeEmpty', t('professionalPubli.main.error.operationAndproperty'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  // 8. LIMPIEZA AL DESEMONTAR - Limpiamos el state del snackbar al desmontar el componente
  useEffect(() => {
    // Saneamiento del state que dispara el Snackbar:
    return () => {
      dispatch(StepperOperations.setSnackAction({ action: '', result: '' }));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snackAction.result]);

  // 9. DETECCIÓN DE CAMBIOS EN EL FORMULARIO
  useEffect(() => {
    setFormIsDirty(formState.isDirty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.isDirty]);

  const getIsValidForm = async () => {
    const res = await trigger();
    return res;
  };

  return (
    <WrapperContainer
      id='mainData-step'
      onSubmit={handleSubmit(onSubmit)}
      title={t('professionalPubli.main.title')}
      note={t('professionalPubli.main.note')}
      formValuesLoading={formValuesLoading || propertyTypesLoading || propertySubTypeLoading}
      loadError={propertyTypesError || formValuesError || propertySubTypeError}
      resetInfo={() => dispatch(StepMainOperations.resetInfo())}
      getIsValidForm={getIsValidForm}
      showSaveAndExitButton={false}
      selectedOperationType={watchedOperationType}
      selectedPropertyType={propertyTypeNameSelected(
        watchedPropertyType,
        propertyTypesMostUsedOptions,
        propertyTypeOptions
      )}
      selectedPropertySubType={propertyTypeNameSelected(effectivePropertySubType, propertySubTypeOptions)}
      enableBackButton={isEnableBackButton}
    >
      <form ref={formRef} id='mainData-step'>
        <fieldset disabled={propertyTypesLoading || formValuesLoading}>
          <WrapperForm data-publi='container-mainData-form'>
            <OperationType
              device={device}
              register={register}
              setValue={(name, value) => handleFieldChange(name, value)}
              watch={watch}
              clearErrors={clearErrors}
              disabled={!!postingId}
            />
            {!!propertyTypesMostUsedOptions && (
              <PropertyType
                device={device}
                register={register}
                setValue={(name, value) => handleFieldChange(name, value)}
                watch={watch}
                clearErrors={clearErrors}
                propertyTypesMostUsedOptions={sortedPropertyTypesMostUsed}
                propertyTypeOptions={sortedPropertyTypes}
                getSubPropertyType={getSubPropertyType}
                disabled={!!postingId}
              />
            )}
            {!!propertyTypesMostUsedOptions && !!watchedPropertyType && (
              <PropertySubType
                device={device}
                register={register}
                setValue={(name, value) => handleFieldChange(name, value)}
                clearErrors={clearErrors}
                propertySubTypeOptions={sortedPropertySubTypes}
                selectedPropertySubType={effectivePropertySubType}
              />
            )}
          </WrapperForm>
        </fieldset>
      </form>
    </WrapperContainer>
  );
};

export default MainData;
